export function extractJsonObject(text: string): string {
  let trimmed = String(text ?? '').trim();
  const codeBlock = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  if (codeBlock && codeBlock[1]) {
    trimmed = codeBlock[1].trim();
  }
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return trimmed;
  }
  const objectMatch = trimmed.match(/(\{[\s\S]*\})/);
  if (objectMatch && objectMatch[1]) {
    return objectMatch[1];
  }
  const arrayMatch = trimmed.match(/(\[[\s\S]*\])/);
  if (arrayMatch && arrayMatch[1]) {
    return arrayMatch[1];
  }
  return trimmed;
}

function getPrevNonWhitespace(source: string, start: number): string | null {
  for (let i = start; i >= 0; i -= 1) {
    const char = source[i];
    if (!/\s/.test(char)) {
      return char;
    }
  }
  return null;
}

function getNextNonWhitespace(source: string, start: number): string | null {
  for (let i = start; i < source.length; i += 1) {
    const char = source[i];
    if (!/\s/.test(char)) {
      return char;
    }
  }
  return null;
}

function sanitizeJsonLike(text: string): string {
  let result = '';
  let inString = false;
  let escape = false;
  const source = text;
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];
    if (inString) {
      if (escape) {
        result += char;
        escape = false;
        continue;
      }
      if (char === '\\') {
        result += char;
        escape = true;
        continue;
      }
      if (char === '"') {
        const next = getNextNonWhitespace(source, i + 1);
        const closesString = next === null || next === ',' || next === '}' || next === ']' || next === ':';
        if (!closesString) {
          result += '\\"';
          continue;
        }
        result += char;
        inString = false;
        continue;
      }
      result += char;
      continue;
    }
    if (char === '"') {
      inString = true;
      result += char;
      continue;
    }
    if (char === 'u' && source.slice(i, i + 9) === 'undefined') {
      const prev = getPrevNonWhitespace(source, i - 1);
      const next = getNextNonWhitespace(source, i + 9);
      const prevOk = prev === null || prev === ':' || prev === '[' || prev === ',';
      const nextOk = next === null || next === ',' || next === '}' || next === ']';
      if (prevOk && nextOk) {
        result += 'null';
        i += 8;
        continue;
      }
    }
    result += char;
  }
  if (inString) {
    result += '"';
  }
  return result;
}

export function parseJsonLike<T>(text: string): T {
  const cleaned = extractJsonObject(text);
  try {
    return JSON.parse(cleaned) as T;
  } catch (err) {
    const sanitized = sanitizeJsonLike(cleaned);
    if (sanitized !== cleaned) {
      return JSON.parse(sanitized) as T;
    }
    throw err;
  }
}

export const __private = {
  getPrevNonWhitespace,
  getNextNonWhitespace,
  sanitizeJsonLike,
};
